import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy, Flame, Star, Zap, Crown, Award, Target,
  Brain, Cpu, Code, FileText, Mic, Users, Heart,
  MessageCircle, Map, Layers, Share2, Rocket, Gem,
  ChevronRight, Lock, Medal, TrendingUp, Calendar
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { gamificationAPI } from '../lib/api';

const ICON_MAP = {
  rocket: Rocket, award: Award, dna: Brain, brain: Brain, cpu: Cpu,
  code: Code, 'file-text': FileText, mic: Mic, flame: Flame,
  zap: Zap, crown: Crown, users: Users, 'message-circle': MessageCircle,
  heart: Heart, star: Star, trophy: Trophy, gem: Gem, map: Map,
  layers: Layers, 'share-2': Share2,
};

const CATEGORY_COLORS = {
  milestone: { bg: '#F5841F', label: 'Milestone' },
  assessment: { bg: '#805AD5', label: 'Assessment' },
  skill: { bg: '#3b82f6', label: 'Skill' },
  streak: { bg: '#f43f5e', label: 'Streak' },
  community: { bg: '#10b981', label: 'Community' },
  social: { bg: '#ec4899', label: 'Social' },
  level: { bg: '#d97706', label: 'Level' },
};

export default function GamificationPage() {
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('badges');

  useEffect(() => {
    const load = async () => {
      try {
        const [profRes, badgesRes, leaderRes] = await Promise.all([
          gamificationAPI.profile(),
          gamificationAPI.badges(),
          gamificationAPI.leaderboard(),
        ]);
        setProfile(profRes.data);
        setBadges(badgesRes.data);
        setLeaderboard(leaderRes.data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load gamification data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#0D9488', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const earnedCount = badges.filter(b => b.earned).length;

  return (
    <div className="space-y-6" data-testid="gamification-page">
      {/* Header */}
      <div>
        <p className="nx-overline mb-1" style={{ color: '#0D9488' }}>Achievements</p>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-none" style={{ color: 'var(--t-text-heading)' }}>
          Badges & Streaks
        </h1>
        <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--t-text-muted)' }}>
          Track your progress, earn badges, and climb the leaderboard
        </p>
      </div>

      {/* Stats Row */}
      {profile && <StatsRow profile={profile} earnedCount={earnedCount} totalBadges={badges.length} />}

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
        {[
          { id: 'badges', icon: Award, label: 'Badges' },
          { id: 'streaks', icon: Flame, label: 'Streaks' },
          { id: 'leaderboard', icon: Trophy, label: 'Leaderboard' },
          { id: 'activity', icon: TrendingUp, label: 'XP Activity' },
        ].map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex-1 justify-center"
            style={{
              background: activeTab === tab.id ? '#0D9488' : 'transparent',
              color: activeTab === tab.id ? '#fff' : 'var(--t-text-muted)',
            }}>
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'badges' && <BadgesGallery badges={badges} />}
      {activeTab === 'streaks' && profile && <StreakTracker profile={profile} />}
      {activeTab === 'leaderboard' && <Leaderboard data={leaderboard} />}
      {activeTab === 'activity' && profile && <XPActivity profile={profile} />}
    </div>
  );
}

// ====== Stats Row ======
function StatsRow({ profile, earnedCount, totalBadges }) {
  const stats = [
    { label: 'Level', value: profile.level, icon: Star, color: '#F5841F', sub: `${profile.level_progress}% to next` },
    { label: 'Total XP', value: profile.total_xp.toLocaleString(), icon: Zap, color: '#3b82f6', sub: `${profile.xp_to_next_level} to level up` },
    { label: 'Streak', value: `${profile.streak_current}d`, icon: Flame, color: '#f43f5e', sub: `Best: ${profile.streak_best}d` },
    { label: 'Badges', value: `${earnedCount}/${totalBadges}`, icon: Award, color: '#805AD5', sub: `${totalBadges - earnedCount} to unlock` },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-row">
      {stats.map((s, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          className="nx-card p-4" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color}12` }}>
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>{s.label}</span>
          </div>
          <p className="text-2xl font-black" style={{ color: 'var(--t-text-heading)' }}>{s.value}</p>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{s.sub}</p>
          {s.label === 'Level' && (
            <div className="mt-2 h-1.5 rounded-full" style={{ background: 'var(--t-border)' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${profile.level_progress}%`, background: s.color }} />
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
}

// ====== Badges Gallery ======
function BadgesGallery({ badges }) {
  const categories = [...new Set(badges.map(b => b.category))];
  const earned = badges.filter(b => b.earned);
  const locked = badges.filter(b => !b.earned);

  return (
    <div className="space-y-6" data-testid="badges-gallery">
      {/* Earned Badges */}
      {earned.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4" style={{ color: '#38A169' }} />
            <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Earned ({earned.length})</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {earned.map((badge, i) => (
              <BadgeCard key={badge.id} badge={badge} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Locked Badges */}
      {locked.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
            <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Locked ({locked.length})</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {locked.map((badge, i) => (
              <BadgeCard key={badge.id} badge={badge} index={i} locked />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BadgeCard({ badge, index, locked = false }) {
  const Icon = ICON_MAP[badge.icon] || Award;
  const catColor = CATEGORY_COLORS[badge.category]?.bg || '#718096';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.04 }}
      data-testid={`badge-${badge.id}`}
      className="nx-card p-4 text-center relative overflow-hidden"
      style={{ opacity: locked ? 0.5 : 1 }}
    >
      {locked && (
        <div className="absolute top-2 right-2">
          <Lock className="w-3 h-3" style={{ color: 'var(--t-text-muted)' }} />
        </div>
      )}
      <div className={`w-12 h-12 rounded-2xl mx-auto mb-2.5 flex items-center justify-center ${locked ? '' : 'animate-pulse-slow'}`}
        style={{
          background: locked ? 'var(--t-bg-section)' : `${catColor}15`,
          border: `2px solid ${locked ? 'var(--t-border)' : `${catColor}30`}`,
        }}>
        <Icon className="w-6 h-6" style={{ color: locked ? 'var(--t-text-muted)' : catColor }} />
      </div>
      <h4 className="text-xs font-bold mb-0.5" style={{ color: locked ? 'var(--t-text-muted)' : 'var(--t-text-heading)' }}>
        {badge.name}
      </h4>
      <p className="text-[9px] leading-tight mb-2" style={{ color: 'var(--t-text-muted)' }}>
        {badge.description}
      </p>
      <Badge className="text-[8px] px-2 py-0"
        style={{
          background: locked ? 'var(--t-bg-section)' : `${catColor}10`,
          color: locked ? 'var(--t-text-muted)' : catColor,
          border: `1px solid ${locked ? 'var(--t-border)' : `${catColor}25`}`,
        }}>
        {CATEGORY_COLORS[badge.category]?.label || badge.category}
      </Badge>
    </motion.div>
  );
}

// ====== Streak Tracker ======
function StreakTracker({ profile }) {
  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay();

  // Build a simple calendar showing streak activity
  const streakStart = new Date();
  streakStart.setDate(streakStart.getDate() - profile.streak_current + 1);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const isStreakDay = (day) => {
    if (!day) return false;
    const date = new Date(today.getFullYear(), today.getMonth(), day);
    return date >= streakStart && date <= today;
  };

  const streakMilestones = [
    { days: 3, label: 'On Fire', icon: Flame, color: '#f43f5e', badge: 'streak_3' },
    { days: 7, label: 'Streak Champion', icon: Zap, color: '#F5841F', badge: 'streak_7' },
    { days: 30, label: 'Unstoppable', icon: Crown, color: '#805AD5', badge: 'streak_30' },
  ];

  return (
    <div className="space-y-4" data-testid="streak-tracker">
      {/* Streak Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          className="nx-card p-6 text-center">
          <Flame className="w-10 h-10 mx-auto mb-3" style={{ color: '#f43f5e' }} />
          <p className="text-5xl font-black" data-testid="current-streak" style={{ color: 'var(--t-text-heading)' }}>
            {profile.streak_current}
          </p>
          <p className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>
            Current Streak (days)
          </p>
          {profile.streak_current >= 7 && (
            <Badge className="mt-2 text-[9px]" style={{ background: '#F5841F15', color: '#F5841F', border: '1px solid #F5841F30' }}>
              1.5x XP Bonus Active
            </Badge>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          className="nx-card p-6 text-center">
          <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: '#d97706' }} />
          <p className="text-5xl font-black" data-testid="best-streak" style={{ color: 'var(--t-text-heading)' }}>
            {profile.streak_best}
          </p>
          <p className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>
            Best Streak (days)
          </p>
        </motion.div>
      </div>

      {/* Streak Milestones */}
      <div className="nx-card p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--t-text-heading)' }}>Streak Milestones</h3>
        <div className="space-y-2.5">
          {streakMilestones.map(m => {
            const reached = profile.streak_current >= m.days;
            const progress = Math.min((profile.streak_current / m.days) * 100, 100);
            return (
              <div key={m.days} className="flex items-center gap-3" data-testid={`milestone-${m.days}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: reached ? `${m.color}15` : 'var(--t-bg-section)', border: `1px solid ${reached ? `${m.color}30` : 'var(--t-border)'}` }}>
                  <m.icon className="w-4.5 h-4.5" style={{ color: reached ? m.color : 'var(--t-text-muted)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold" style={{ color: reached ? 'var(--t-text-heading)' : 'var(--t-text-muted)' }}>
                      {m.label} ({m.days}d)
                    </span>
                    <span className="text-[10px] font-bold" style={{ color: reached ? m.color : 'var(--t-text-muted)' }}>
                      {reached ? 'Unlocked' : `${profile.streak_current}/${m.days}`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: 'var(--t-border)' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: m.color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Calendar */}
      <div className="nx-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4" style={{ color: '#F5841F' }} />
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-heading)' }}>
            {today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-[9px] font-bold py-1" style={{ color: 'var(--t-text-muted)' }}>{d}</div>
          ))}
          {calendarDays.map((day, i) => {
            const active = isStreakDay(day);
            const isToday = day === today.getDate();
            return (
              <div key={i}
                className={`text-center py-1.5 rounded-lg text-[10px] font-bold ${isToday ? 'ring-1' : ''}`}
                style={{
                  background: active ? '#f43f5e15' : 'transparent',
                  color: active ? '#f43f5e' : day ? 'var(--t-text-secondary)' : 'transparent',
                  ringColor: isToday ? '#F5841F' : undefined,
                }}>
                {day || ''}
                {active && <div className="w-1 h-1 rounded-full mx-auto mt-0.5" style={{ background: '#f43f5e' }} />}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 pt-2" style={{ borderTop: '1px solid var(--t-border-subtle)' }}>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#f43f5e15', border: '1px solid #f43f5e30' }} />
            <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>Active streak day</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm ring-1" style={{ ringColor: '#F5841F' }} />
            <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== Leaderboard ======
function Leaderboard({ data }) {
  const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];

  return (
    <div className="nx-card overflow-hidden" data-testid="leaderboard">
      <div className="p-4" style={{ borderBottom: '1px solid var(--t-border-subtle)' }}>
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5" style={{ color: '#F5841F' }} />
          <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Top Players</h3>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="p-8 text-center">
          <Trophy className="w-8 h-8 mx-auto mb-2 opacity-20" style={{ color: 'var(--t-text-muted)' }} />
          <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>No leaderboard data yet. Start earning XP!</p>
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--t-border-subtle)' }}>
          {data.map((user, i) => (
            <motion.div key={user.user_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center gap-3 px-4 py-3" data-testid={`leaderboard-row-${i}`}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-black"
                style={{
                  background: i < 3 ? `${medals[i]}20` : 'var(--t-bg-section)',
                  color: i < 3 ? medals[i] : 'var(--t-text-muted)',
                  border: `2px solid ${i < 3 ? `${medals[i]}40` : 'var(--t-border)'}`,
                }}>
                {i < 3 ? <Medal className="w-4 h-4" /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{user.name}</p>
                <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                  Level {user.level} &middot; {user.badge_count} badges
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-black" style={{ color: '#F5841F' }}>{user.total_xp.toLocaleString()}</p>
                <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>XP</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== XP Activity ======
function XPActivity({ profile }) {
  const history = profile.recent_xp || [];

  return (
    <div className="nx-card overflow-hidden" data-testid="xp-activity">
      <div className="p-4" style={{ borderBottom: '1px solid var(--t-border-subtle)' }}>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" style={{ color: '#F5841F' }} />
          <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Recent XP Activity</h3>
        </div>
      </div>
      {history.length === 0 ? (
        <div className="p-8 text-center">
          <Zap className="w-8 h-8 mx-auto mb-2 opacity-20" style={{ color: 'var(--t-text-muted)' }} />
          <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>No XP earned yet. Complete assessments, save resumes, or log in daily to earn XP!</p>
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--t-border-subtle)' }}>
          {history.map((entry, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="flex items-center gap-3 px-4 py-3" data-testid={`xp-entry-${i}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#0D948812' }}>
                <Zap className="w-4 h-4" style={{ color: '#F5841F' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>{entry.label}</p>
                <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                </p>
              </div>
              <span className="text-sm font-black" style={{ color: '#38A169' }}>
                +{entry.xp} XP
              </span>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
